import { useState, useEffect, useRef } from 'react';
import vcontrolAPI from '../../../services/documentApi';
import {
  getCachedThumbnail,
  cacheThumbnail,
  removeThumbnailFromCache,
} from '../../Replan/utils/thumbnailCache';

/**
 * HomePageThumb - Mini page thumbnail for the Annotated Pages widget.
 * Mirrors the cache + abortable fetch pattern used by PageSelectorModal's
 * PageThumbnail, but trimmed down (no IntersectionObserver, no shape badges).
 */
const HomePageThumb = ({ documentId, actualPageNumber, label, pageIndex, onSelect }) => {
  const [url, setUrl] = useState(() =>
    documentId && actualPageNumber
      ? getCachedThumbnail(documentId, actualPageNumber)
      : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const abortRef = useRef(null);

  useEffect(() => {
    // Cancel any in-flight request when documentId/pageNumber changes
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setUrl(
      documentId && actualPageNumber
        ? getCachedThumbnail(documentId, actualPageNumber) || null
        : null,
    );
    setIsLoading(false);
    setError(false);
    setReloadKey(0);
  }, [documentId, actualPageNumber]);

  useEffect(() => {
    if (!documentId || !actualPageNumber) return;

    const cached = getCachedThumbnail(documentId, actualPageNumber);
    if (cached) {
      setUrl(cached);
      setIsLoading(false);
      setError(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setError(false);

    vcontrolAPI
      .getThumbnail(documentId, actualPageNumber, { signal: controller.signal })
      .then((blob) => {
        if (controller.signal.aborted) return;
        const objectUrl = URL.createObjectURL(blob);
        cacheThumbnail(documentId, actualPageNumber, objectUrl);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        console.error(
          `Failed to load thumbnail for page ${actualPageNumber}:`,
          err,
        );
        setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [documentId, actualPageNumber, reloadKey]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
    },
    [],
  );

  const handleRetry = (e) => {
    e.stopPropagation();
    removeThumbnailFromCache(documentId, actualPageNumber);
    setUrl(null);
    setError(false);
    setReloadKey((k) => k + 1);
  };

  const handleClick = (e) => {
    if (error) return; // error state has its own click handler
    if (onSelect) onSelect({ documentId, actualPageNumber, label, pageIndex });
  };

  const titleText = label || `Pg ${(pageIndex ?? 0) + 1}`;

  return (
    <div
      className="home-thumb-mini"
      title={error ? 'Click to retry' : `${titleText} — open in Replan`}
      onClick={handleClick}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect({ documentId, actualPageNumber, label, pageIndex });
              }
            }
          : undefined
      }
    >
      {url ? (
        <img
          src={url}
          alt={titleText}
          className="home-thumb-mini-img"
          loading="lazy"
          onError={() => setError(true)}
        />
      ) : error ? (
        <div
          className="home-thumb-placeholder"
          style={{ cursor: 'pointer' }}
          onClick={handleRetry}
          title="Click to retry"
        >
          ⚠
        </div>
      ) : isLoading ? (
        <div
          className="home-thumb-placeholder placeholder-line"
          style={{ borderRadius: 0, margin: 0 }}
        />
      ) : (
        <div className="home-thumb-placeholder">{titleText}</div>
      )}
    </div>
  );
};

export default HomePageThumb;
